/**
 * The live-imperative-modal registry, deliberately kept in its OWN
 * dependency-free module.
 *
 * `src/setupTests.ts` calls `destroyAll()` after every test, and importing it
 * from `./imperative` would drag `ImperativeConfirm` → the Radix AlertDialog →
 * the icon layer into the module graph of every test file in the repo, whether
 * or not it ever opens a modal. This module imports nothing.
 *
 * Why the teardown exists at all: an imperative modal renders into its own
 * body-level React root, NOT into the container `@testing-library/react`
 * creates, so RTL's `cleanup()` cannot unmount it. A modal left open leaks
 * into the next test with Radix's `pointer-events: none` still on `<body>`,
 * and every `userEvent` interaction there then fails with "element has
 * pointer-events: none" — blaming the innocent test.
 *
 * Ported from the `update-design` reference branch at the same path.
 */

export type LiveModal = {
  /**
   * Immediate, timer-independent teardown, and the only thing the registry
   * needs. `destroyAll` must NOT go through the modal's own animated close
   * path, whose unmount is deferred with `setTimeout` — under
   * `vi.useFakeTimers()` that timer never fires and the root stays mounted,
   * which is the exact leak this registry exists to prevent.
   */
  dispose: () => void
}

const live = new Set<LiveModal>()

export const registerModal = (modal: LiveModal): void => {
  live.add(modal)
}

export const unregisterModal = (modal: LiveModal): void => {
  live.delete(modal)
}

/** The shadcn half of antd's `Modal.destroyAll()`. Synchronous — see `LiveModal.dispose`. */
export const destroyAllShadcnModals = (): void => {
  const pending = Array.from(live)
  live.clear()
  pending.forEach((modal) => modal.dispose())
}
